import { ref, reactive, computed, watch } from 'vue'
import type { Maquette, MaquetteRequest } from '@/types/maquette'
import { useApi } from '@/composables/useApi'
import { useClasses } from '@/composables/useClasses'
import { useECGlobals } from '@/composables/useECGlobals'
import { toast } from 'vue3-toastify'
import { extractApiErrorMessage } from '@/utils/apiError'
import { applyHistoriqueParams, shouldSkipActiveFilter } from '@/utils/historiqueParams'
import { useProfileStore } from '@/stores/profile'
import { usePermissions } from '@/composables/usePermissions'
import { mapEcMaquetteForForm, pedagogicalEcPayload } from '@/utils/ecMaquetteSnapshot'

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  errors?: Record<string, string[]>;
}

export function useMaquettes() {
  const { apiData } = useApi()
  const { fetchAllECGlobals } = useECGlobals()
  const classStore = useClasses()
  const profileStore = useProfileStore()
  const { isSuperAdmin } = usePermissions()
  const state = reactive({
    maquettes: [] as Maquette[],
    maquette: null as Maquette | null,
    loading: false,
    errors: {} as Record<string, string[]>,
    filters: {
      search: '',
      classe_id: null as number | null,
      session_id: null as number | null,
      classe_specialite_id: null as number | null,
      is_active: 'Tous' as 'Tous' | 'Actifs' | 'Inactifs',
      page: 1,
      per_page: 20
    },
    pagination: {
      total: 0,
      current_page: 1,
      per_page: 20
    },
    allMaquettes: [] as Maquette[]
  })

  const loadingState = ref(false)
  /** Mode historique local à l'instance (survît aux refetch des watchers). */
  const includeHistorique = ref(false)
  const anneeUniversitaire = ref<string | null>(null)

  const filteredMaquettes = computed(() => {
    return state.maquettes.filter(maquette => {
      const matchSearch = !state.filters.search ||
          maquette.code.toLowerCase().includes(state.filters.search.toLowerCase()) ||
          maquette.nom.toLowerCase().includes(state.filters.search.toLowerCase());

      const matchStatus = state.filters.is_active === 'Tous' ||
          shouldSkipActiveFilter({
            includeHistorique: includeHistorique.value,
            anneeUniversitaire: anneeUniversitaire.value,
          }) ||
          (state.filters.is_active === 'Actifs' && maquette.is_active) ||
          (state.filters.is_active === 'Inactifs' && !maquette.is_active);

      const matchClasse = !state.filters.classe_id ||
          maquette.classe_id === state.filters.classe_id;

      const matchSession = !state.filters.session_id ||
          maquette.session_id === state.filters.session_id;

      const matchSpecialite = !state.filters.classe_specialite_id ||
          maquette.classe_specialite_id === state.filters.classe_specialite_id ||
          (maquette.classe_specialite && maquette.classe_specialite.id === state.filters.classe_specialite_id) ||
          (maquette.specialite && maquette.specialite.id === state.filters.classe_specialite_id);

      return matchSearch && matchStatus && matchClasse && matchSession && matchSpecialite;
    });
  });

  let searchTimeout: NodeJS.Timeout | null = null
  let filtersTimeout: NodeJS.Timeout | null = null

  // Watcher pour la recherche avec debounce
  watch(
      () => state.filters.search,
      (newVal) => {
        if (searchTimeout) clearTimeout(searchTimeout)
        searchTimeout = setTimeout(() => {
          state.filters.page = 1 // Réinitialiser la page lors d'une nouvelle recherche
          fetchMaquettes()
        }, 300) // Délai de 300ms
      }
  )

  // Watcher avec debounce pour éviter les requêtes multiples lors des changements de filtres
  // Supprimer les watchers individuels pour classe_id et is_active car ils sont déjà couverts
  // par le watcher deep dans la page. On les garde seulement si nécessaire côté client.

  // Ne pas refetch sur page/per_page: pagination côté client

  const headers = [
    { title: 'Nom', key: 'nom', sortable: true },
    { title: 'Code', key: 'code', sortable: true },
    { title: 'Classe', key: 'classe', sortable: true },
    { title: 'Statut', key: 'is_active', sortable: true },
    { title: 'Actions', key: 'actions', sortable: false }
  ]

  function formatApiErrorMessage(res: any, fallback = "Une erreur s'est produite"): string {
    return extractApiErrorMessage(res, fallback)
  }

  async function fetchMaquettes(options?: {
    per_page?: number
    withRelations?: boolean
    includeDeliberationMaquettes?: boolean
    includeHistorique?: boolean
    anneeUniversitaire?: string | null
  }) {
    loadingState.value = true
    try {
      if (options?.includeHistorique !== undefined) {
        includeHistorique.value = options.includeHistorique
      }
      if (options?.anneeUniversitaire !== undefined) {
        anneeUniversitaire.value = options.anneeUniversitaire
      }
      const params = new URLSearchParams()
      // Uniformiser: charger tout
      params.append('per_page', 'all')
      // Charger les UEs + semestres (le backend attend with_ues, pas le paramètre Spatie "with")
      if (options?.withRelations === true) {
        params.append('with_ues', 'true')
      }
      // Inclure les maquettes des délibérations seulement si explicitement demandé (pour les pages de délibération)
      if (options?.includeDeliberationMaquettes === true) {
        params.append('include_deliberation_maquettes', 'true')
      }
      // Mode historique : include_historique OU annee_universitaire
      applyHistoriqueParams(params, {
        includeHistorique: includeHistorique.value,
        anneeUniversitaire: anneeUniversitaire.value,
      })
      const res = await apiData<ApiResponse<any>>(`/maquettes?${params.toString()}`)
      if (res?.success) {
        let maquettes = []
        if (Array.isArray(res.data)) {
          maquettes = res.data
        } else if (Array.isArray(res.data?.data)) {
          maquettes = res.data.data
        }
        
        // Mettre à jour state.maquettes (maquettes filtrées)
        state.maquettes = maquettes
        state.pagination = { total: maquettes.length, current_page: 1, per_page: state.filters.per_page }
        
        // Optimisation : mettre aussi à jour allMaquettes avec les mêmes données pour éviter une deuxième requête
        state.allMaquettes = maquettes
      } else {
        state.maquettes = []
        state.allMaquettes = []
        state.pagination = { total: 0, current_page: 1, per_page: state.filters.per_page }
      }
    } catch (error) {
      state.maquettes = []
      state.allMaquettes = []
    } finally {
      loadingState.value = false
    }
  }

  async function fetchAllMaquettes(withRelations: boolean = false, options?: { includeHistorique?: boolean; anneeUniversitaire?: string | null }) {
    state.loading = true
    try {
      if (options?.includeHistorique !== undefined) {
        includeHistorique.value = options.includeHistorique
      }
      if (options?.anneeUniversitaire !== undefined) {
        anneeUniversitaire.value = options.anneeUniversitaire
      }
      // Optimisation : si on a déjà les maquettes dans state.maquettes, les réutiliser
      // pour éviter une requête redondante (sauf en mode historique, qui a un périmètre différent)
      if (state.maquettes.length > 0 && !withRelations && !includeHistorique.value && !anneeUniversitaire.value) {
        state.allMaquettes = state.maquettes
        state.loading = false
        return
      }
      
      // Sinon, charger depuis l'API
      const params = new URLSearchParams()
      params.append('per_page', 'all')
      if (withRelations) {
        params.append('with_ues', 'true')
      }
      applyHistoriqueParams(params, {
        includeHistorique: includeHistorique.value,
        anneeUniversitaire: anneeUniversitaire.value,
      })
      const res = await apiData<ApiResponse<any>>(`/maquettes?${params.toString()}`)
      let maquettes = []
      if (Array.isArray(res?.data?.data)) {
        maquettes = res.data.data
      } else if (Array.isArray(res?.data)) {
        maquettes = res.data
      }
      state.allMaquettes = maquettes
    } finally {
      state.loading = false
    }
  }

  async function fetchMaquette(id: number) {
    state.loading = true
    try {
      // Pour Admin/SuperAdmin, envoyer explicitement le header X-Selected-Group avec "Admin"
      // ou ne pas l'envoyer du tout selon la logique du backend
      const userGroups = profileStore.profile?.groups || []
      const hasAdminGroup = userGroups.some((g: any) => g.name === 'Admin')
      const isAdminOrSuperAdmin = isSuperAdmin.value || hasAdminGroup
      
      // Construire les options avec headers
      const options: any = {}
      
      // Pour Admin/SuperAdmin, envoyer explicitement "Admin" dans le header
      // Cela indique au backend que l'utilisateur doit avoir accès complet
      if (isAdminOrSuperAdmin) {
        options['X-Selected-Group'] = 'Admin'
      }
      // Pour les autres utilisateurs, ne pas envoyer le header
      // Le backend gérera selon leurs groupes
      
      const res = await apiData<ApiResponse<Maquette>>(`/maquettes/${id}/complete`, options)
      
      if (res?.success) {
        return res.data
      }
      return null
    } catch (error: any) {
      // Si erreur 403 et que l'utilisateur est Admin, c'est un problème backend
      const userGroups = profileStore.profile?.groups || []
      const hasAdminGroup = userGroups.some((g: any) => g.name === 'Admin')
      if (error?.statusCode === 403 && (isSuperAdmin.value || hasAdminGroup)) {
        console.warn('Erreur 403 pour un utilisateur Admin - Le backend doit être corrigé pour reconnaître l\'accès Admin')
        toast.error('Accès refusé. Le backend doit être configuré pour reconnaître les utilisateurs Admin.')
      }
      throw error
    } finally {
      state.loading = false
    }
  }

  /**
   * Charger plusieurs maquettes complètes en une seule requête (batch)
   * @param options Options de chargement batch
   * @returns Tableau de maquettes complètes
   */
  async function fetchMaquettesBatch(options: {
    ids?: number[]
    session_id?: number
    /** Plusieurs sessions : une seule requête retourne toutes les maquettes (chaque maquette a session_id). */
    session_ids?: number[]
    /** Une spécialité : réponse 0 ou 1 maquette. À envoyer avec session_id pour délib/prédélib/bulletin. */
    specialite_id?: number
    /** Plusieurs spécialités : réponse N maquettes. */
    specialite_ids?: number[]
    with_plannings?: boolean
    with_seances?: boolean
    includeDeliberationMaquettes?: boolean
    includeHistorique?: boolean
    anneeUniversitaire?: string | null
    headers?: Record<string, string>
    /** Si true, propage l'erreur réseau au lieu de retourner []. */
    throwOnError?: boolean
  } = {}): Promise<Maquette[]> {
    const {
      ids,
      session_id,
      session_ids,
      specialite_id,
      specialite_ids,
      with_plannings = true,
      with_seances = false,
      includeDeliberationMaquettes = false,
      includeHistorique: includeHistoriqueOpt,
      anneeUniversitaire: anneeUniversitaireOpt,
      headers = {},
      throwOnError = false,
    } = options

    if (includeHistoriqueOpt !== undefined) {
      includeHistorique.value = includeHistoriqueOpt
    }
    if (anneeUniversitaireOpt !== undefined) {
      anneeUniversitaire.value = anneeUniversitaireOpt
    }

    try {

      const payload: any = {
        with_plannings,
        with_seances
      }

      if (includeDeliberationMaquettes) {
        payload.include_deliberation_maquettes = true
      }

      // Lookup ciblé (ids / session) : inclure l'historique par défaut pour
      // retrouver les maquettes de sessions clôturées même si le mode global est off.
      const targetedLookup = !!(
        (ids && ids.length > 0) ||
        session_id ||
        (session_ids && session_ids.length > 0)
      )
      const annee = anneeUniversitaire.value?.trim() || null
      if (annee) {
        payload.annee_universitaire = annee
      } else if (includeHistorique.value || (targetedLookup && includeHistoriqueOpt === undefined)) {
        payload.include_historique = 1
      }

      if (ids && ids.length > 0) {
        payload.ids = ids
      }

      if (session_ids && session_ids.length > 0) {
        payload.session_ids = session_ids
      } else if (session_id) {
        payload.session_id = session_id
      }

      if (specialite_id != null) {
        payload.specialite_id = specialite_id
      }
      if (specialite_ids?.length) {
        payload.specialite_ids = specialite_ids
      }

      // Construire les options de la requête avec les headers
      const requestOptions: any = {
        method: 'POST',
        body: payload
      }

      // Ajouter les headers personnalisés (les headers commençant par X- seront injectés automatiquement par useApi)
      if (Object.keys(headers).length > 0) {
        Object.keys(headers).forEach(key => {
          requestOptions[key] = headers[key]
        })
      }

      const res = await apiData<ApiResponse<Maquette[]>>(
        '/maquettes/batch-complete',
        requestOptions
      )

      if (res?.success && Array.isArray(res.data)) {
        return res.data
      }
      return []
    } catch (error: any) {
      console.error('Erreur lors du chargement batch des maquettes:', error)
      if (throwOnError) {
        throw error
      }
      return []
    }
  }

  async function createMaquette(data: MaquetteRequest) {
    // Nettoyer complètement les erreurs précédentes
    state.errors = {}
    state.loading = true
    try {
      // Déterminer si la classe liée est une Certification
      async function isCertificationClasse(classeId?: number | null): Promise<boolean> {
        if (!classeId) return false
        // Charger les classes si vide
        if (!Array.isArray(classStore.classes.value) || classStore.classes.value.length === 0) {
          await classStore.fetchClasses()
        }
        const classe = classStore.classes.value.find((c: any) => c && c.id === classeId)
        const niveau = (classe?.niveau || '').toString().trim().toUpperCase()
        return niveau.includes('CERTIFICATION')
      }

      const skipVHTValidation = await isCertificationClasse(data.classe_id)

      const payload = {
        ...data,
        ues: data.ues?.map(ue => ({
          ...ue,
          type_ue: ue.type_ue || 'commune',
          // Inclure le code UE s'il existe et n'est pas vide
          ...(ue.code && ue.code.trim() !== '' && { code: ue.code }),
          ecs: ue.ecs?.map(ec => {
            const ecData: any = {
              id: ec.id,
              ec_global_id: ec.ec_global_id,
              // personnel_id retiré - sera géré séparément via assignPersonnelToMaquette
              evaluation_progressive: ec.evaluation_progressive ?? 40,
              evaluation_finale: ec.evaluation_finale ?? 60,
              is_active: ec.is_active,
              ...pedagogicalEcPayload(ec)
            }
            // Ajouter le code EC s'il existe et n'est pas vide
            if (ec.code && ec.code.trim() !== '') ecData.code = ec.code
            return ecData
          })
        }))
      }
      
      // Récupérer tous les ECs globaux pour la validation
      const ecGlobals = await fetchAllECGlobals()
      
      // Validation VHT avant envoi (ignorée pour les classes Certification)
      if (!skipVHTValidation) {
        const vhtValidation = validateVHTForUEs(data.ues || [], ecGlobals)
        if (!vhtValidation.isValid) {
          const msg = Object.values(vhtValidation.errors).flat().join('\n')
          toast.error(msg)
          return { success: false, message: msg, isVHTError: true }
        }
      }
      
      const res = await apiData<ApiResponse<Maquette>>('/maquettes/complete', { method: 'POST', body: payload })
      if (res.success) {
        // Optimisation : fetchMaquettes remplit aussi allMaquettes, donc pas besoin de fetchAllMaquettes séparément
        await fetchMaquettes()
        // Ne pas afficher le toast ici - il sera affiché après l'assignation du personnel
        return { 
          success: true, 
          message: res.message || 'Maquette créée avec succès',
          data: res.data // Inclure les données pour récupérer l'ID
        }
      }
      
      // GESTION DES ERREURS DE L'API
      if (res.errors && typeof res.errors === 'object') {
        state.errors = res.errors
        const errorMessage = Object.values(res.errors).flat().join('\n') || res.message || "Erreur de validation"
        toast.error(errorMessage)
        return { success: false, message: errorMessage, errors: res.errors }
      }
      
      // Si pas d'erreurs mais pas de succès, afficher le message d'erreur détaillé si disponible
      if (!res.success) {
        const detailed = formatApiErrorMessage(res)
        toast.error(detailed)
        return { success: false, message: detailed }
      }
      
      toast.error("Une erreur s'est produite lors de la création")
      return { success: false, message: "Une erreur s'est produite lors de la création" }
    } catch (error: any) {
      const payload = error?.response?._data
      const detailed = payload ? formatApiErrorMessage(payload) : (error?.message || "Erreur réseau ou inattendue")
      toast.error(detailed)
      return { success: false, message: detailed }
    } finally {
      state.loading = false
    }
  }

  async function updateMaquette(id: number, data: MaquetteRequest) {
    // Nettoyer complètement les erreurs précédentes
    state.errors = {}
    state.loading = true
    try {
      // Déterminer si la classe liée est une Certification
      async function isCertificationClasse(classeId?: number | null): Promise<boolean> {
        if (!classeId) return false
        if (!Array.isArray(classStore.classes.value) || classStore.classes.value.length === 0) {
          await classStore.fetchClasses()
        }
        const classe = classStore.classes.value.find((c: any) => c && c.id === classeId)
        const niveau = (classe?.niveau || '').toString().trim().toUpperCase()
        return niveau.includes('CERTIFICATION')
      }

      const skipVHTValidation = await isCertificationClasse(data.classe_id)

      // Construction du payload propre (pas de champs superflus)
      const payload = {
        nom: data.nom,
        classe_id: data.classe_id,
        session_id: data.session_id,
        classe_specialite_id: data.classe_specialite_id,
        ues: data.ues?.map(ue => {
          const uePayload: any = {
            intitule: ue.intitule,
            credit: ue.credit,
            type_ue: ue.type_ue || 'commune',
            semestre_id: ue.semestre_id,
            // Ajouter le code UE s'il existe et n'est pas vide
            ...(ue.code && ue.code.trim() !== '' && { code: ue.code }),
            ecs: ue.ecs?.map(ec => {
              const ecPayload: any = {
                ec_global_id: ec.ec_global_id,
                // personnel_id retiré - sera géré séparément via assignPersonnelToMaquette
                evaluation_progressive: Number(ec.evaluation_progressive ?? 40),
                evaluation_finale: Number(ec.evaluation_finale ?? 60),
                ...pedagogicalEcPayload(ec)
              }
              // Ajouter le code EC s'il existe et n'est pas vide
              if (ec.code && ec.code.trim() !== '') ecPayload.code = ec.code
              if (ec.id) ecPayload.id = ec.id
              return ecPayload
            })
          }
          if (ue.id) uePayload.id = ue.id
          return uePayload
        })
      }
      
      // Récupérer tous les ECs globaux pour la validation
      const ecGlobals = await fetchAllECGlobals()
      
      // Validation VHT avant envoi (ignorée pour les classes Certification)
      if (!skipVHTValidation) {
        const vhtValidation = validateVHTForUEs(data.ues || [], ecGlobals)
        if (!vhtValidation.isValid) {
          const msg = Object.values(vhtValidation.errors).flat().join('\n')
          toast.error(msg)
          return { success: false, message: msg, isVHTError: true }
        }
      }
      
      const res = await apiData<ApiResponse<Maquette>>(`/maquettes/${id}/complete`, { method: 'PUT', body: payload })

      if (res.success) {
        // Optimisation : fetchMaquettes remplit aussi allMaquettes, donc pas besoin de fetchAllMaquettes séparément
        await fetchMaquettes()
        // Ne pas afficher le toast ici - il sera affiché après l'assignation du personnel
        return { success: true, message: res.message || 'Maquette mise à jour avec succès' }
      }

      // GESTION DES ERREURS DE L'API
      if (res.errors) {
        state.errors = res.errors
        const errorMessage = Object.values(res.errors).flat().join('\n') || res.message || 'Erreur lors de la mise à jour'
        toast.error(errorMessage)
        return { success: false, message: errorMessage, errors: res.errors }
      }

      // Si pas d'erreurs mais pas de succès, afficher le message d'erreur détaillé (incluant ues_concernees, etc.)
      if (!res.success) {
        const detailed = formatApiErrorMessage(res)
        toast.error(detailed)
        return { success: false, message: detailed }
      }

      return { success: false, message: 'Erreur lors de la mise à jour' }
    } catch (error: any) {
      // Gestion des erreurs HTTP avec détails
      const payload = error?.response?._data
      const detailed = payload ? formatApiErrorMessage(payload) : (error?.message || 'Erreur lors de la mise à jour de la maquette')
      toast.error(detailed)
      return { success: false, message: detailed }
    } finally {
      state.loading = false
    }
  }

  async function deleteMaquette(id: number) {
    state.loading = true
    try {
      const response = await apiData<ApiResponse<null>>(`/maquettes/${id}`, { method: 'DELETE' })
      if (response.success) {
        // Optimisation : fetchMaquettes remplit aussi allMaquettes, donc pas besoin de fetchAllMaquettes séparément
        await fetchMaquettes()
        toast.success(response.message || 'Maquette supprimée avec succès')
        return { success: true, message: response.message || 'Maquette supprimée avec succès', toastShown: true }
      }
      const detailed = formatApiErrorMessage(response, response?.message || "Une erreur s'est produite lors de la suppression")
      const normalizedDetail = detailed.toLowerCase()
      const isMaquetteActiveError = normalizedDetail.includes('maquette active existe')

      toast.error(detailed)
      return {
        success: false,
        message: detailed,
        toastShown: true,
        ...(isMaquetteActiveError ? { isMaquetteActiveError: true } : {})
      }
    } catch (error: any) {
      // Gestion des erreurs HTTP
      const payload = error?.response?._data || error?.data || error
      const detailed = formatApiErrorMessage(payload, "Erreur réseau ou inattendue")
      const normalizedDetail = detailed.toLowerCase()
      const isMaquetteActiveError = normalizedDetail.includes('maquette active existe')

      toast.error(detailed)
      if (normalizedDetail.includes('foreign key constraint') || normalizedDetail.includes('integrity constraint')) {
        return {
          success: false,
          message: "Impossible de supprimer cette maquette car elle est utilisée par d'autres éléments",
          toastShown: true
        }
      }
      return {
        success: false,
        message: detailed,
        toastShown: true,
        ...(isMaquetteActiveError ? { isMaquetteActiveError: true } : {})
      }
    } finally {
      state.loading = false
    }
  }

  async function duplicateMaquette(id: number, params: {
    classe_id: number,
    session_id?: number,
    classe_specialite_id?: number|null,
    copier_ues_ecs?: boolean,
    nom?: string,
    ues_semestres?: Array<{ ue_id: number, semestre_id: number }>
  }) {
    try {
      const response = await apiData<ApiResponse<Maquette>>(
          `/maquettes/${id}/duplicate`,
          { method: 'POST', body: params }
      )
      if (response.success) {
        // Optimisation : fetchMaquettes remplit aussi allMaquettes, donc pas besoin de fetchAllMaquettes séparément
        await fetchMaquettes()
        return { 
          success: true, 
          message: response.message || 'Maquette dupliquée avec succès',
          data: response.data // Inclure les données pour récupérer l'ID
        }
      }
      return { success: false, message: response.message || "Une erreur s'est produite lors de la duplication", data: null }
    } catch (error: any) {
      const errorMessage = error?.response?._data?.message || error?.data?.message || "Erreur réseau ou inattendue"
      return { success: false, message: errorMessage, data: null }
    } finally {
      state.loading = false
    }
  }

  async function toggleActiveMaquette(id: number) {
    state.loading = true
    try {
      const res = await apiData<ApiResponse<Maquette>>(`/maquettes/${id}/toggle-active`, { method: 'PATCH' })
      if (res.success) {
        // Optimisation : fetchMaquettes remplit aussi allMaquettes, donc pas besoin de fetchAllMaquettes séparément
        await fetchMaquettes()
        toast.success(res.message || 'Statut modifié')
        return { success: true, message: res.message || 'Statut modifié' }
      }
      toast.error(res.message || "Erreur lors de la modification du statut")
      return { success: false, message: res.message || "Erreur lors de la modification du statut" }
    } catch (error: any) {
      const errorMessage = error?.response?._data?.message || error?.data?.message || "Erreur réseau ou inattendue"
      toast.error(errorMessage)
      return { success: false, message: errorMessage }
    } finally {
      state.loading = false
    }
  }

  function resetForm(item?: Maquette) {
    state.errors = {}
    if (!item) {
      return { nom: '', code: '', classe_id: null, is_active: true, ues: [] }
    }
    return {
      nom: item.nom,
      code: item.code,
      classe_id: item.classe_id,
      is_active: item.is_active,
      ues: item.ues?.map(ue => ({
        code: ue.code,
        intitule: ue.intitule,
        credit: ue.credit,
        semestre_id: ue.semestre_id,
        type_ue: ue.type_ue || 'commune',
        is_active: ue.is_active,
        ecs: ue.ecs?.map(ec => ({
          ...mapEcMaquetteForForm(ec),
          personnel_id: ec.personnel_id,
        })) || []
      })) || []
    }
  }

  function errorsClear() {
    state.errors = {}
  }

  // Nouvelle fonction de validation VHT
  function validateVHTForUEs(ues: any[], ecGlobals: any[] = []): { isValid: boolean; errors: Record<string, string[]> } {
    const vhtErrors: Record<string, string[]> = {}
    let isValid = true

    if (!ues || !Array.isArray(ues)) {
      return { isValid: false, errors: { general: ['Aucune UE trouvée'] } }
    }

    ues.forEach((ue, ueIndex) => {
      if (!ue.ecs || !Array.isArray(ue.ecs) || ue.ecs.length === 0) {
        vhtErrors[`ue_${ueIndex}_ecs`] = ['Au moins un EC est requis pour cette UE']
        isValid = false
        return
      }

      // Calculer le VHT total des ECs de cette UE
      let totalVHT = 0
      let hasValidECs = false
      let ecDetails: string[] = []
      
      ue.ecs.forEach((ec: any) => {
        if (ec.ec_global_id) {
          // Récupérer l'EC Global pour obtenir les volumes
          const ecGlobal = ecGlobals.find(eg => eg.id === ec.ec_global_id)
          
          if (ecGlobal || ec.volume_vht != null) {
            hasValidECs = true
            
            const ecVHT = Number(ec.volume_vht ?? 0)
            totalVHT += ecVHT
            
            const label = ec.code || ec.intitule || ecGlobal?.code || ecGlobal?.intitule || `EC ${ec.ec_global_id}`
            ecDetails.push(`${label}: ${ecVHT}h`)
          }
        }
      })

      if (!hasValidECs) {
        vhtErrors[`ue_${ueIndex}_ecs`] = ['Aucun EC Global valide trouvé pour cette UE']
        isValid = false
        return
      }

      // Vérifier que le VHT total = crédit × 20
      const expectedVHT = (ue.credit || 0) * 20
      
      if (totalVHT !== expectedVHT) {
        const difference = totalVHT - expectedVHT
        const action = difference > 0 ? 'diminuer' : 'augmenter'
        const ueCode = ue.code || `UE ${ueIndex + 1}`
        const message = `UE "${ue.intitule}" (${ueCode}): Le volume horaire total des ECs (${totalVHT}h) doit être égal au crédit × 20 (${expectedVHT}h). ` +
                       `Différence: ${Math.abs(difference)}h. ` +
                       `Détail des ECs: ${ecDetails.join(', ')}. ` +
                       `Vous devez ${action} le crédit ou modifier les ECs.`
        
        vhtErrors[`ue_${ueIndex}_vht`] = [message]
        isValid = false
      }
    })

    return { isValid, errors: vhtErrors }
  }

  async function fetchMaquetteByClasseAndSemestre(classeId: number, semestreId: number) {
    state.loading = true
    try {
      const res = await apiData<ApiResponse<Maquette>>(`/maquettes/classe/${classeId}/semestre/${semestreId}`)
      if (res?.success) {
        return res.data
      }
      return null
    } finally {
      state.loading = false
    }
  }

  /**
   * Assigner du personnel aux ECs d'une maquette
   */
  async function assignPersonnelToMaquette(id: number, assignments: Array<{ ec_id: number, personnel_id: number | null }>) {
    state.loading = true
    try {
      
      const res = await apiData<ApiResponse<Maquette>>(`/maquettes/${id}/assign-personnel`, { 
        method: 'PATCH', 
        body: { assignments } 
      })

      if (res.success) {
        // Optimisation : fetchMaquettes remplit aussi allMaquettes, donc pas besoin de fetchAllMaquettes séparément
        await fetchMaquettes()
        
        // Recharger la maquette spécifique pour avoir les données à jour
        // Utiliser le batch endpoint même pour une seule maquette pour optimiser les grandes maquettes
        try {
          const batchResult = await fetchMaquettesBatch({
            ids: [id],
            with_plannings: false,
            with_seances: false
          })
          if (batchResult.length > 0) {
            const refreshedMaquette = batchResult[0]
            // Mettre à jour le state avec les nouvelles données
            const maquetteIndex = state.allMaquettes.findIndex(m => m.id === id)
            if (maquetteIndex !== -1) {
              state.allMaquettes[maquetteIndex] = refreshedMaquette
            }
          }
        } catch (error) {
          // console.warn('⚠️ [useMaquettes] Erreur rechargement maquette après assignation:', error)
        }
        
        toast.success(res.message || 'Personnel assigné avec succès')
        return { success: true, message: res.message || 'Personnel assigné avec succès' }
      }

      if (res.errors) {
        state.errors = res.errors
        const errorMessage = Object.values(res.errors).flat().join('\n') || res.message || 'Erreur lors de l\'assignation'
        toast.error(errorMessage)
        return { success: false, message: errorMessage, errors: res.errors }
      }

      if (!res.success) {
        const detailed = formatApiErrorMessage(res)
        toast.error(detailed)
        return { success: false, message: detailed }
      }

      return { success: false, message: 'Erreur lors de l\'assignation du personnel' }
    } catch (error: any) {
      const payload = error?.response?._data
      const detailed = payload ? formatApiErrorMessage(payload) : (error?.message || 'Erreur lors de l\'assignation du personnel')
      toast.error(detailed)
      return { success: false, message: detailed }
    } finally {
      state.loading = false
    }
  }

  /**
   * Assigner du personnel à une maquette après création (pour les nouveaux ECs)
   */
  async function assignPersonnelToNewMaquette(maquetteId: number, uesData: Array<{ ecs?: Array<{ personnel_id?: number | null }> }>) {
    const assignments: Array<{ ec_id: number, personnel_id: number | null }> = []
    
    // Récupérer la maquette avec ses ECs pour obtenir les IDs
    // Utiliser le batch endpoint même pour une seule maquette pour optimiser les grandes maquettes
    const batchResult = await fetchMaquettesBatch({
      ids: [maquetteId],
      with_plannings: false,
      with_seances: false
    })
    if (batchResult.length === 0) {
      return { success: false, message: 'Impossible de récupérer la maquette créée' }
    }
    const maquette = batchResult[0]
    
    // Mapper les assignations avec les nouveaux IDs d'ECs
    let ecIndex = 0
    uesData.forEach(ueData => {
      if (ueData.ecs) {
        ueData.ecs.forEach(ecData => {
          if (ecData.personnel_id !== null && ecData.personnel_id !== undefined) {
            // Trouver l'EC correspondant dans la maquette récupérée
            const correspondingEC = maquette.ues?.find(ue => ue.id)?.ecs?.find((_, index) => index === ecIndex)
            if (correspondingEC) {
              assignments.push({
                ec_id: correspondingEC.id,
                personnel_id: ecData.personnel_id
              })
            }
          }
          ecIndex++
        })
      }
    })
    
    if (assignments.length > 0) {
      return await assignPersonnelToMaquette(maquetteId, assignments)
    }
    
    return { success: true, message: 'Aucune assignation nécessaire' }
  }

  return {
    maquettes: filteredMaquettes,
    allMaquettes: computed(() => state.allMaquettes),
    maquette: computed(() => state.maquette),
    loading: loadingState,
    errors: computed(() => state.errors),
    filters: state.filters,
    pagination: state.pagination,
    headers,
    includeHistorique,
    anneeUniversitaire,
    fetchMaquettes,
    fetchAllMaquettes,
    fetchMaquette,
    fetchMaquettesBatch,
    createMaquette,
    updateMaquette,
    deleteMaquette,
    duplicateMaquette,
    toggleActiveMaquette,
    assignPersonnelToMaquette,
    assignPersonnelToNewMaquette,
    resetForm,
    errorsClear,
    validateVHTForUEs,
    fetchMaquetteByClasseAndSemestre
  }
} 